import {
  DocumentationItem,
  DocumentationListElementsResponse,
  DocumentationSearchResponse,
  DocumentationDependenciesResponse,
  DocumentationDependentsResponse,
} from './documentation';

export enum HubspotParentType {
  SCHEMA = 'hubspot.schema',
  WORKFLOW = 'hubspot.workflow',
  PIPELINE = 'hubspot.pipeline',
  LIST = 'hubspot.list',
  FORM = 'hubspot.form',
  MARKETING_EMAIL = 'hubspot.marketing_email',
  OWNER = 'hubspot.owner',
  USER = 'hubspot.user',
  SEQUENCE = 'hubspot.sequence',
  PRODUCT = 'hubspot.product',
  DOMAIN = 'hubspot.domain',
}

/** Child element types returned from drill-down /children (not top-level documentation tabs). */
export enum HubspotChildType {
  PROPERTY = 'hubspot.property',
  PROPERTY_GROUP = 'hubspot.property_group',
  PIPELINE_STAGE = 'hubspot.pipeline_stage',
}

export type HubspotTabType = HubspotParentType | HubspotChildType;

export interface HubspotItemBase extends DocumentationItem {
  description?: string;
  body?: string;
}

export interface HubspotSchemaItem extends HubspotItemBase {
  parentType: HubspotParentType.SCHEMA;
  objectTypeId?: string;
  isCustom?: string;
  propertyCount?: string;
}

export interface HubspotWorkflowItem extends HubspotItemBase {
  parentType: HubspotParentType.WORKFLOW;
  flowType?: string;
  workflowType?: string;
  isEnabled?: string;
  objectTypeId?: string;
}

export interface HubspotPipelineItem extends HubspotItemBase {
  parentType: HubspotParentType.PIPELINE;
  objectTypeId?: string;
  stageCount?: string;
}

export interface HubspotListItem extends HubspotItemBase {
  parentType: HubspotParentType.LIST;
  listType?: string;
  objectTypeId?: string;
  memberCount?: string;
}

export interface HubspotFormItem extends HubspotItemBase {
  parentType: HubspotParentType.FORM;
  formType?: string;
}

export interface HubspotMarketingEmailItem extends HubspotItemBase {
  parentType: HubspotParentType.MARKETING_EMAIL;
  emailType?: string;
  status?: string;
}

export interface HubspotOwnerItem extends HubspotItemBase {
  parentType: HubspotParentType.OWNER;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface HubspotUserItem extends HubspotItemBase {
  parentType: HubspotParentType.USER;
  email?: string;
  roleId?: string;
}

export interface HubspotSequenceItem extends HubspotItemBase {
  parentType: HubspotParentType.SEQUENCE;
  isPublished?: string;
}

export interface HubspotProductItem extends HubspotItemBase {
  parentType: HubspotParentType.PRODUCT;
  sku?: string;
  price?: string;
}

export interface HubspotDomainItem extends HubspotItemBase {
  parentType: HubspotParentType.DOMAIN;
  primary?: string;
  subdomain?: string;
}

export interface HubspotPropertyItem extends HubspotItemBase {
  parentType: HubspotChildType.PROPERTY;
  fieldType?: string;
  groupName?: string;
  objectType?: string;
  propertyType?: string;
  calculated?: string;
  hasUniqueValue?: string;
}

export interface HubspotPropertyGroupItem extends HubspotItemBase {
  parentType: HubspotChildType.PROPERTY_GROUP;
  displayOrder?: string;
}

export interface HubspotPipelineStageItem extends HubspotItemBase {
  parentType: HubspotChildType.PIPELINE_STAGE;
  pipelineId?: string;
  objectType?: string;
  displayOrder?: string;
}

export type HubspotConfigurationItem =
  | HubspotSchemaItem
  | HubspotWorkflowItem
  | HubspotPipelineItem
  | HubspotListItem
  | HubspotFormItem
  | HubspotMarketingEmailItem
  | HubspotOwnerItem
  | HubspotUserItem
  | HubspotSequenceItem
  | HubspotProductItem
  | HubspotDomainItem
  | HubspotPropertyItem
  | HubspotPropertyGroupItem
  | HubspotPipelineStageItem;

export type HubspotDocumentationListElementsResponse =
  DocumentationListElementsResponse<HubspotConfigurationItem>;
export type HubspotDocumentationSearchResponse =
  DocumentationSearchResponse<HubspotConfigurationItem>;
export type HubspotDocumentationDependenciesResponse =
  DocumentationDependenciesResponse<HubspotConfigurationItem>;
export type HubspotDocumentationDependentsResponse =
  DocumentationDependentsResponse<HubspotConfigurationItem>;

export interface HubspotDocumentationChildrenResponse {
  children: Record<string, HubspotConfigurationItem[]>;
}
